import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import MainLayout from "../components/mainLayout";
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  MessageCircle,
  RefreshCw,
  Send,
  ShieldAlert,
  TimerReset,
  Eye,
  XCircle,
  Ban,
  History,
  Loader2,
  Settings,
  Trash2,
  Save,
  Power,
  ShieldCheck,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "../sass/pages/dashboardCobrancaWhatsapp.scss";

const COBRANCA_API_BASE_URL =
  "https://api-cobranca-production-f162.up.railway.app";

const cobrancaApi = axios.create({
  baseURL: COBRANCA_API_BASE_URL,
  timeout: 30000,
});


const COLORS = {
  sucesso: "#22c55e",
  atencao: "#f59e0b",
  erro: "#ef4444",
  info: "#38bdf8",
  neutro: "#94a3b8",
  roxo: "#8b5cf6",
};

const statusFilaMap = {
  PENDENTE: { label: "Pendente", className: "warning", color: COLORS.atencao },
  PROCESSANDO: { label: "Processando", className: "info", color: COLORS.info },
  ENVIADO: { label: "Enviado", className: "success", color: COLORS.sucesso },
  ERRO: { label: "Erro", className: "danger", color: COLORS.erro },
  IGNORADO: { label: "Ignorado", className: "muted", color: COLORS.neutro },
};

const statusTwilioMap = {
  queued: { label: "Na fila Twilio", className: "info", color: COLORS.info },
  sent: { label: "Enviado", className: "info", color: COLORS.info },
  delivered: { label: "Entregue", className: "success", color: COLORS.sucesso },
  read: { label: "Lido", className: "success", color: COLORS.sucesso },
  failed: { label: "Falhou", className: "danger", color: COLORS.erro },
  undelivered: { label: "Não entregue", className: "danger", color: COLORS.erro },
  sem_status: { label: "Sem status", className: "muted", color: COLORS.neutro },
};

const TWILIO_ERROR_CATALOG = {
  63024: {
    code: "63024",
    title: "Destinatário inválido no WhatsApp",
    shortMessage:
      "A Meta/Twilio não conseguiu entregar porque o destinatário não está apto para receber WhatsApp.",
    possibleCauses: [
      "Número não possui WhatsApp ativo.",
      "Cliente ainda não aceitou os Termos de Serviço/Privacidade do WhatsApp.",
      "Cliente usa uma versão antiga ou incompatível do WhatsApp.",
      "Meta aplicou restrição no destinatário ou no canal de entrega.",
    ],
    operationalAction:
      "Valide o telefone com o cliente. Se confirmado como inválido, inclua na blacklist para evitar novas tentativas automáticas.",
    retryGuidance:
      "Não reprocessar em lote antes de validar o número. Reenvio sem correção tende a falhar novamente.",
    tone: "danger",
  },
};

function formatEtapaRegua(value) {
  const etapa = String(value || "").trim().toUpperCase();

  if (etapa === "D+1") {
    return "D+1 (histórico)";
  }

  return value || "-";
}

function normalizarPayloadJson(value) {
  if (!value) return null;

  if (typeof value === "object") {
    return value;
  }

  if (typeof value !== "string") {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function montarTextoErroTwilio(source) {
  if (!source) return "";

  if (typeof source === "string" || typeof source === "number") {
    return String(source);
  }

  const camposDiretos = [
    "errorCode",
    "erroCodigo",
    "codigoErro",
    "twilioErrorCode",
    "erroTwilioCodigo",
    "ultimoErro",
    "mensagem",
    "message",
    "statusMessage",
    "statusTwilio",
    "tipoErro",
    "erro",
    "error",
    "reason",
    "failureReason",
  ];

  const partes = [];

  camposDiretos.forEach((campo) => {
    if (source[campo] !== undefined && source[campo] !== null) {
      partes.push(String(source[campo]));
    }
  });

  const payload =
    normalizarPayloadJson(source.payloadJson) ||
    normalizarPayloadJson(source.payload_json) ||
    normalizarPayloadJson(source.payload);

  if (payload) {
    [
      "ErrorCode",
      "errorCode",
      "ErrorMessage",
      "errorMessage",
      "MessageStatus",
      "SmsStatus",
      "status",
      "message",
    ].forEach((campo) => {
      if (payload[campo] !== undefined && payload[campo] !== null) {
        partes.push(String(payload[campo]));
      }
    });

    try {
      partes.push(JSON.stringify(payload));
    } catch {
      // Ignora payload não serializável.
    }
  }

  return partes.join(" ");
}

function extrairCodigoErroTwilio(source) {
  const texto = montarTextoErroTwilio(source);

  if (!texto) return null;

  const match = texto.match(/\b(63\d{3})\b/);

  return match?.[1] || null;
}

function getTwilioErrorInfo(source) {
  const code = extrairCodigoErroTwilio(source);

  if (!code) return null;

  return TWILIO_ERROR_CATALOG[code] || {
    code,
    title: `Erro Twilio ${code}`,
    shortMessage:
      "Falha retornada pela Twilio/Meta. Consulte a timeline e o painel da Twilio para diagnóstico detalhado.",
    possibleCauses: [
      "Falha operacional do canal.",
      "Restrição do destinatário.",
      "Parâmetro ou template recusado pela plataforma.",
    ],
    operationalAction:
      "Avalie o erro antes de reprocessar em lote para evitar novas falhas.",
    retryGuidance: "Reprocessar somente após validação operacional.",
    tone: "warning",
  };
}

function getErroOperacionalResumo(source, fallback = "Erro não detalhado") {
  const info = getTwilioErrorInfo(source);

  if (info) {
    return `${info.code} - ${info.title}. ${info.shortMessage}`;
  }

  return source?.ultimoErro || source?.mensagem || source?.message || fallback;
}

function TwilioErrorInsight({ source, compact = false }) {
  const info = getTwilioErrorInfo(source);

  if (!info) return null;

  if (compact) {
    return (
      <div className={`cw-error-insight compact ${info.tone}`}>
        <strong>{info.code} • {info.title}</strong>
        <small>{info.shortMessage}</small>
      </div>
    );
  }

  return (
    <div className={`cw-error-insight ${info.tone}`}>
      <strong>{info.code} • {info.title}</strong>
      <span>{info.shortMessage}</span>

      <ul>
        {info.possibleCauses.map((cause) => (
          <li key={cause}>{cause}</li>
        ))}
      </ul>

      <em>{info.operationalAction}</em>
      <small>{info.retryGuidance}</small>
    </div>
  );
}


function formatNumber(value) {
  return Number(value || 0).toLocaleString("pt-BR");
}

function formatPercent(value) {
  return `${Number(value || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`;
}

function formatDateTime(value) {
  if (!value) return "-";
  const data = new Date(value);
  if (Number.isNaN(data.getTime())) return String(value);
  return data.toLocaleString("pt-BR");
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function getStatusFila(status) {
  return statusFilaMap[String(status || "").toUpperCase()] || {
    label: status || "-",
    className: "muted",
    color: COLORS.neutro,
  };
}

function getStatusTwilio(status) {
  return statusTwilioMap[String(status || "").toLowerCase()] || {
    label: status || "-",
    className: "muted",
    color: COLORS.neutro,
  };
}

function KpiCard({ title, value, description, icon, tone = "info", extra }) {
  return (
    <div className={`cw-kpi-card ${tone}`}>
      <div className="cw-kpi-top">
        <div className="cw-kpi-icon">{icon}</div>
        {extra && <div className="cw-kpi-extra">{extra}</div>}
      </div>
      <div className="cw-kpi-content">
        <span className="cw-kpi-title">{title}</span>
        <strong className="cw-kpi-value">{value}</strong>
        <small className="cw-kpi-description">{description}</small>
      </div>
    </div>
  );
}

function StatusBadge({ type = "fila", status }) {
  const config = type === "twilio" ? getStatusTwilio(status) : getStatusFila(status);

  return (
    <span className={`cw-status-badge ${config.className}`}>
      <span className="cw-status-dot" />
      {config.label}
    </span>
  );
}

const DashboardCobrancaWhatsapp = () => {
  const [resumo, setResumo] = useState(null);
  const [fila, setFila] = useState([]);
  const [logs, setLogs] = useState([]);
  const [falhas, setFalhas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [erro, setErro] = useState("");
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const [timelineAberta, setTimelineAberta] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [filaSelecionada, setFilaSelecionada] = useState(null);
  const [blacklist, setBlacklist] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [loadingBlacklist, setLoadingBlacklist] = useState(false);

  const [modalOperacao, setModalOperacao] = useState(null);
  const [modalBusy, setModalBusy] = useState(false);

  const [formBlacklist, setFormBlacklist] = useState({
    telefone: "",
    motivo: "SOLICITACAO_CLIENTE",
    observacao: "",
  });

  const [formRemoverBlacklist, setFormRemoverBlacklist] = useState({
    telefone: "",
  });

  const [formConfig, setFormConfig] = useState({
    envioAtivo: "N",
    limiteLote: "5",
  });

  const [formProcessamento, setFormProcessamento] = useState({
    limit: 1,
  });

  const [filtros, setFiltros] = useState({
    status: "TODOS",
    etapaRegua: "TODOS",
    limit: 50,
  });

  const totais = resumo?.totais || {};

  const carregarDashboard = async () => {
    setLoading(true);
    setErro("");

    try {
      const paramsFila = {
        limit: filtros.limit,
      };

      if (filtros.status !== "TODOS") {
        paramsFila.status = filtros.status;
      }

      if (filtros.etapaRegua !== "TODOS") {
        paramsFila.etapaRegua = filtros.etapaRegua;
      }

      const [resumoRes, filaRes, logsRes, falhasRes, blacklistRes] = await Promise.all([
        cobrancaApi.get("/admin-cobranca/dashboard/resumo"),
        cobrancaApi.get("/admin-cobranca/dashboard/fila", { params: paramsFila }),
        cobrancaApi.get("/admin-cobranca/dashboard/logs", { params: { limit: 30 } }),
        cobrancaApi.get("/admin-cobranca/dashboard/falhas", { params: { limit: 30 } }),
        cobrancaApi.get("/admin-cobranca/blacklist", { params: { limit: 10, ativo: "S" } }),
      ]);

      setResumo(resumoRes.data?.dados || {});
      setFila(filaRes.data?.dados || []);
      setLogs(logsRes.data?.dados || []);
      setFalhas(falhasRes.data?.dados || []);
      setBlacklist(blacklistRes.data?.dados?.dados || blacklistRes.data?.dados || []);
      setUltimaAtualizacao(new Date());
    } catch (error) {
      setErro(
        error?.response?.data?.mensagem ||
          error?.message ||
          "Erro ao carregar dashboard de cobrança.",
      );
    } finally {
      setLoading(false);
    }
  };

  const processarFilaManual = async (limit = 1) => {
    const confirmar = window.confirm(
      `Confirma o processamento manual de ${limit} mensagem(ns) da fila?`,
    );

    if (!confirmar) return;

    setProcessing(true);
    setErro("");

    try {
      await cobrancaApi.post("/admin-cobranca/fila/processar", { limit });
      await carregarDashboard();
    } catch (error) {
      setErro(
        error?.response?.data?.mensagem ||
          error?.message ||
          "Erro ao processar fila manualmente.",
      );
    } finally {
      setProcessing(false);
    }
  };

  const carregarTimeline = async (filaItem) => {
    const filaId = filaItem?.id || filaItem;

    if (!filaId) return;

    setLoadingTimeline(true);
    setErro("");
    setFilaSelecionada(filaItem || { id: filaId });
    setTimelineAberta(true);

    try {
      const response = await cobrancaApi.get(`/admin-cobranca/fila/${filaId}/detalhe`);
      const dados = response.data?.dados || {};
      setTimeline(dados.timeline || dados.logs || []);
      setFilaSelecionada(dados.fila || filaItem || { id: filaId });
    } catch (error) {
      setErro(
        error?.response?.data?.mensagem ||
          error?.message ||
          "Erro ao carregar timeline da cobrança.",
      );
      setTimeline([]);
    } finally {
      setLoadingTimeline(false);
    }
  };

  const carregarBlacklist = async () => {
    setLoadingBlacklist(true);
    setErro("");

    try {
      const response = await cobrancaApi.get("/admin-cobranca/blacklist", {
        params: { limit: 20, ativo: "S" },
      });

      setBlacklist(response.data?.dados?.dados || response.data?.dados || []);
    } catch (error) {
      setErro(
        error?.response?.data?.mensagem ||
          error?.message ||
          "Erro ao carregar blacklist.",
      );
    } finally {
      setLoadingBlacklist(false);
    }
  };

  const reprocessarItem = async (id) => {
    const confirmar = window.confirm(`Confirma o reprocessamento do item #${id}?`);

    if (!confirmar) return;

    setProcessing(true);
    setErro("");

    try {
      await cobrancaApi.post(`/admin-cobranca/fila/${id}/reprocessar`);
      await carregarDashboard();
    } catch (error) {
      setErro(
        error?.response?.data?.mensagem ||
          error?.message ||
          "Erro ao reprocessar item da fila.",
      );
    } finally {
      setProcessing(false);
    }
  };

  const reprocessarFalhas = async () => {
    const confirmar = window.confirm("Confirma o reprocessamento das falhas elegíveis?");

    if (!confirmar) return;

    setProcessing(true);
    setErro("");

    try {
      await cobrancaApi.post("/admin-cobranca/fila/reprocessar-falhas", { limit: 20 });
      await carregarDashboard();
    } catch (error) {
      setErro(
        error?.response?.data?.mensagem ||
          error?.message ||
          "Erro ao reprocessar falhas.",
      );
    } finally {
      setProcessing(false);
    }
  };

  const abrirModalOperacao = (tipo, payload = {}) => {
    setErro("");

    if (tipo === "removerBlacklist" && payload.telefone) {
      setFormRemoverBlacklist({ telefone: payload.telefone });
    }

    setModalOperacao(tipo);
  };

  const fecharModalOperacao = () => {
    if (modalBusy) return;
    setModalOperacao(null);
  };

  const incluirBlacklistManual = async (event) => {
    event.preventDefault();

    if (!formBlacklist.telefone.trim()) {
      setErro("Informe o telefone para incluir na blacklist.");
      return;
    }

    setModalBusy(true);
    setErro("");

    try {
      await cobrancaApi.post("/admin-cobranca/blacklist", {
        telefone: formBlacklist.telefone,
        motivo: formBlacklist.motivo || "BLOQUEIO_MANUAL",
        observacao: formBlacklist.observacao || null,
      });

      setFormBlacklist({
        telefone: "",
        motivo: "SOLICITACAO_CLIENTE",
        observacao: "",
      });

      await carregarBlacklist();
      await carregarDashboard();

      setModalOperacao(null);
    } catch (error) {
      setErro(
        error?.response?.data?.mensagem ||
          error?.message ||
          "Erro ao incluir telefone na blacklist.",
      );
    } finally {
      setModalBusy(false);
    }
  };

  const removerBlacklistManual = async (event) => {
    event.preventDefault();

    if (!formRemoverBlacklist.telefone.trim()) {
      setErro("Informe o telefone para remover da blacklist.");
      return;
    }

    setModalBusy(true);
    setErro("");

    try {
      await cobrancaApi.delete("/admin-cobranca/blacklist", {
        data: {
          telefone: formRemoverBlacklist.telefone,
        },
      });

      setFormRemoverBlacklist({ telefone: "" });

      await carregarBlacklist();
      await carregarDashboard();

      setModalOperacao(null);
    } catch (error) {
      setErro(
        error?.response?.data?.mensagem ||
          error?.message ||
          "Erro ao remover telefone da blacklist.",
      );
    } finally {
      setModalBusy(false);
    }
  };

  const salvarConfigOperacional = async (event) => {
    event.preventDefault();

    setModalBusy(true);
    setErro("");

    try {
      await cobrancaApi.put("/admin-cobranca/config", {
        configs: [
          {
            chave: "ENVIO_ATIVO",
            valor: formConfig.envioAtivo,
          },
          {
            chave: "LIMITE_PROCESSAMENTO_LOTE",
            valor: String(formConfig.limiteLote || "5"),
          },
        ],
      });

      await carregarDashboard();

      setModalOperacao(null);
    } catch (error) {
      setErro(
        error?.response?.data?.mensagem ||
          error?.message ||
          "Erro ao atualizar configurações operacionais.",
      );
    } finally {
      setModalBusy(false);
    }
  };

  const processarFilaPeloModal = async (event) => {
    event.preventDefault();

    const limit = Number(formProcessamento.limit || 1);

    if (!Number.isFinite(limit) || limit <= 0) {
      setErro("Informe um limite válido para processamento.");
      return;
    }

    const confirmar = window.confirm(`Confirma o processamento manual de ${limit} mensagem(ns)?`);

    if (!confirmar) return;

    setModalBusy(true);
    setProcessing(true);
    setErro("");

    try {
      await cobrancaApi.post("/admin-cobranca/fila/processar", { limit });
      await carregarDashboard();
      setModalOperacao(null);
    } catch (error) {
      setErro(
        error?.response?.data?.mensagem ||
          error?.message ||
          "Erro ao processar fila manualmente.",
      );
    } finally {
      setModalBusy(false);
      setProcessing(false);
    }
  };

  useEffect(() => {
    carregarDashboard();

    const interval = setInterval(() => {
      carregarDashboard();
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusFilaChart = useMemo(() => {
    return (resumo?.porStatusFila || []).map((item) => {
      const config = getStatusFila(item.status);

      return {
        name: config.label,
        status: item.status,
        total: Number(item.total || 0),
        color: config.color,
      };
    });
  }, [resumo]);

  const statusTwilioChart = useMemo(() => {
    return (resumo?.porStatusTwilio || []).map((item) => {
      const config = getStatusTwilio(item.statusTwilio);

      return {
        name: config.label,
        status: item.statusTwilio,
        total: Number(item.total || 0),
        color: config.color,
      };
    });
  }, [resumo]);

  const etapasChart = useMemo(() => {
    return (resumo?.porEtapa || []).map((item) => ({
      name: formatEtapaRegua(item.etapaRegua),
      etapaRegua: item.etapaRegua,
      total: Number(item.total || 0),
    }));
  }, [resumo]);

  if (loading && !resumo) {
    return (
      <MainLayout>
        <div className="dashboard-cobranca-whatsapp">
          <div className="cw-header-skeleton">
            <Skeleton height={34} width={320} />
            <Skeleton height={34} width={160} />
          </div>
          <Skeleton height={90} count={1} style={{ marginBottom: 20 }} />
          <Skeleton height={140} count={2} style={{ marginBottom: 20 }} />
          <Skeleton height={280} count={2} style={{ marginBottom: 20 }} />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="dashboard-cobranca-whatsapp">
        <header className="cw-header">
          <div>
            <div className="cw-eyebrow">Operação / Cobrança automatizada</div>
            <h1>Dashboard Cobrança WhatsApp</h1>
            <p>
              Monitoramento operacional da régua ativa D-3, D+5 e D+10,
              fila de envio, callbacks Twilio, falhas e entregas.
            </p>
          </div>

          <div className="cw-header-actions">
            <div className="cw-live-badge">
              <span />
              Operacional
            </div>

            <button
              type="button"
              className="cw-btn secondary"
              onClick={carregarDashboard}
              disabled={loading || processing}
            >
              <RefreshCw size={16} />
              Atualizar
            </button>
          </div>
        </header>

        {erro && (
          <div className="cw-alert danger">
            <AlertTriangle size={18} />
            <span>{erro}</span>
          </div>
        )}

        <div className="cw-alert info">
          <ShieldCheck size={18} />
          <span>
            Régua ativa: D-3, D+5 e D+10. D+1 foi desativado e aparece apenas
            como histórico operacional.
          </span>
        </div>

        <section className="cw-control-panel">
          <div className="cw-filter">
            <label>Status da fila</label>
            <select
              value={filtros.status}
              onChange={(e) =>
                setFiltros((prev) => ({ ...prev, status: e.target.value }))
              }
            >
              <option value="TODOS">Todos</option>
              <option value="PENDENTE">Pendente</option>
              <option value="PROCESSANDO">Processando</option>
              <option value="ENVIADO">Enviado</option>
              <option value="ERRO">Erro</option>
              <option value="IGNORADO">Ignorado</option>
            </select>
          </div>

          <div className="cw-filter">
            <label>Etapa da régua</label>
            <select
              value={filtros.etapaRegua}
              onChange={(e) =>
                setFiltros((prev) => ({ ...prev, etapaRegua: e.target.value }))
              }
            >
              <option value="TODOS">Todas</option>
              <option value="D-3">D-3</option>
              <option value="D+1">D+1 (histórico)</option>
              <option value="D+5">D+5</option>
              <option value="D+10">D+10</option>
            </select>
          </div>

          <div className="cw-filter">
            <label>Limite</label>
            <select
              value={filtros.limit}
              onChange={(e) =>
                setFiltros((prev) => ({ ...prev, limit: Number(e.target.value) }))
              }
            >
              <option value={30}>30 registros</option>
              <option value={50}>50 registros</option>
              <option value={100}>100 registros</option>
              <option value={200}>200 registros</option>
            </select>
          </div>

          <div className="cw-control-actions">
            <button
              type="button"
              className="cw-btn secondary"
              onClick={carregarDashboard}
              disabled={loading || processing}
            >
              Filtrar
            </button>

            <button
              type="button"
              className="cw-btn warning"
              onClick={() => processarFilaManual(1)}
              disabled={loading || processing}
            >
              <Send size={16} />
              Enviar 1
            </button>

            <button
              type="button"
              className="cw-btn danger-outline"
              onClick={() => processarFilaManual(10)}
              disabled={loading || processing}
            >
              <ShieldAlert size={16} />
              Enviar 10
            </button>

            <button
              type="button"
              className="cw-btn warning"
              onClick={reprocessarFalhas}
              disabled={loading || processing}
            >
              <TimerReset size={16} />
              Reprocessar falhas
            </button>

            <button
              type="button"
              className="cw-btn secondary"
              onClick={() => abrirModalOperacao("processar")}
              disabled={loading || processing}
            >
              <Send size={16} />
              Operação manual
            </button>

            <button
              type="button"
              className="cw-btn secondary"
              onClick={() => abrirModalOperacao("config")}
              disabled={loading || processing}
            >
              <Settings size={16} />
              Configurações
            </button>

            <button
              type="button"
              className="cw-btn danger-outline"
              onClick={() => abrirModalOperacao("incluirBlacklist")}
              disabled={loading || processing}
            >
              <Ban size={16} />
              Bloquear telefone
            </button>
          </div>
        </section>

        <section className="cw-kpi-grid">
          <KpiCard
            title="Pendentes"
            value={formatNumber(totais.pendentes)}
            description="Mensagens aguardando processamento"
            tone="warning"
            icon={<Clock size={22} />}
          />

          <KpiCard
            title="Enviados hoje"
            value={formatNumber(totais.enviadosHoje)}
            description="Disparos realizados no dia"
            tone="info"
            icon={<Send size={22} />}
          />

          <KpiCard
            title="Entregues"
            value={formatNumber(totais.entregues)}
            description={`Taxa de entrega: ${formatPercent(totais.taxaEntrega)}`}
            tone="success"
            icon={<CheckCircle size={22} />}
          />

          <KpiCard
            title="Lidos"
            value={formatNumber(totais.lidos)}
            description="Confirmações de leitura recebidas"
            tone="success"
            icon={<Eye size={22} />}
          />

          <KpiCard
            title="Falhas"
            value={formatNumber(totais.falhas || totais.erros)}
            description={`Taxa de falha: ${formatPercent(totais.taxaFalha)}`}
            tone="danger"
            icon={<XCircle size={22} />}
          />

          <KpiCard
            title="Total na fila"
            value={formatNumber(totais.totalFila)}
            description="Volume total gerado pela régua"
            tone="neutral"
            icon={<MessageCircle size={22} />}
          />
        </section>

        <section className="cw-health-strip">
          <div className="cw-health-item success">
            <span>Webhook Twilio</span>
            <strong>Ativo</strong>
          </div>

          <div className="cw-health-item info">
            <span>Logs Twilio</span>
            <strong>{formatNumber(totais.totalLogs)}</strong>
          </div>

          <div className="cw-health-item warning">
            <span>Ignorados</span>
            <strong>{formatNumber(totais.ignorados)}</strong>
          </div>

          <div className="cw-health-item neutral">
            <span>Última atualização</span>
            <strong>{ultimaAtualizacao ? formatDateTime(ultimaAtualizacao) : "-"}</strong>
          </div>
        </section>

        <section className="cw-panel cw-blacklist-panel">
          <div className="cw-panel-header">
            <div>
              <h2>Blacklist / LGPD</h2>
              <p>Telefones bloqueados para envio automático.</p>
            </div>

            <button
              type="button"
              className="cw-btn secondary"
              onClick={carregarBlacklist}
              disabled={loadingBlacklist}
            >
              {loadingBlacklist ? <Loader2 size={16} /> : <RefreshCw size={16} />}
              Atualizar blacklist
            </button>
          </div>

          <div className="cw-blacklist-grid">
            {loadingBlacklist && (
              <div className="cw-empty">Carregando blacklist...</div>
            )}

            {!loadingBlacklist && blacklist.length === 0 && (
              <div className="cw-empty success">Nenhum telefone bloqueado.</div>
            )}

            {!loadingBlacklist &&
              blacklist.slice(0, 10).map((item) => (
                <div key={item.id} className="cw-blacklist-item">
                  <div className="cw-blacklist-icon">
                    <Ban size={16} />
                  </div>

                  <div>
                    <strong>{item.telefone || item.telefoneNormalizado}</strong>
                    <span>{item.motivo || "Sem motivo"}</span>
                    <small>{item.observacao || "Sem observação"}</small>
                  </div>

                  <div className="cw-blacklist-actions">
                    <small>{formatDateTime(item.criadoEm)}</small>

                    <button
                      type="button"
                      className="cw-mini-btn danger"
                      title="Remover da blacklist"
                      onClick={() =>
                        abrirModalOperacao("removerBlacklist", {
                          telefone: item.telefone || item.telefoneNormalizado,
                        })
                      }
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </section>

        <section className="cw-charts-grid">
          <div className="cw-panel">
            <div className="cw-panel-header">
              <div>
                <h2>Status da fila</h2>
                <p>Distribuição dos registros operacionais.</p>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={statusFilaChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="total" radius={[8, 8, 0, 0]}>
                  {statusFilaChart.map((item) => (
                    <Cell key={item.name} fill={item.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="cw-panel">
            <div className="cw-panel-header">
              <div>
                <h2>Status Twilio</h2>
                <p>Retornos reais recebidos via webhook.</p>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={statusTwilioChart}
                  dataKey="total"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label
                >
                  {statusTwilioChart.map((item) => (
                    <Cell key={item.name} fill={item.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="cw-panel cw-panel-wide">
            <div className="cw-panel-header">
              <div>
                <h2>Volume por etapa da régua</h2>
                <p>Distribuição por D-3, D+5 e D+10. D+1 aparece apenas como histórico.</p>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={etapasChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="total" fill={COLORS.roxo} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="cw-section-grid">
          <div className="cw-panel">
            <div className="cw-panel-header">
              <div>
                <h2>Últimos eventos Twilio</h2>
                <p>Leitura operacional dos callbacks recebidos.</p>
              </div>
            </div>

            <div className="cw-list">
              {logs.length === 0 && (
                <div className="cw-empty">Nenhum log encontrado.</div>
              )}

              {logs.slice(0, 10).map((log) => (
                <div key={log.id} className="cw-list-item">
                  <div className="cw-list-main">
                    <strong>{log.nomeCliente || "Cliente não informado"}</strong>
                    <span>{log.telefone || "-"}</span>
                    <small>{log.twilioSid}</small>
                  </div>

                  <div className="cw-list-side">
                    <StatusBadge type="twilio" status={log.statusTwilio} />
                    <small>{formatDateTime(log.atualizadoEm || log.criadoEm)}</small>
                  </div>

                  <TwilioErrorInsight source={log} compact />
                </div>
              ))}
            </div>
          </div>

          <div className="cw-panel danger-panel">
            <div className="cw-panel-header">
              <div>
                <h2>Falhas recentes</h2>
                <p>Itens com erro técnico ou operacional.</p>
              </div>
            </div>

            <div className="cw-list">
              {falhas.length === 0 && (
                <div className="cw-empty success">
                  Nenhuma falha crítica no momento.
                </div>
              )}

              {falhas.slice(0, 10).map((item) => (
                <div key={item.id} className="cw-list-item danger">
                  <div className="cw-list-main">
                    <strong>{item.nomeCliente || "Cliente não informado"}</strong>
                    <span>
                      {item.etapaRegua} • Tentativas: {item.tentativas || 0}
                    </span>
                    <small>{getErroOperacionalResumo(item)}</small>
                    <TwilioErrorInsight source={item} compact />
                  </div>

                  <div className="cw-list-side">
                    <StatusBadge status={item.status} />
                    <small>{formatDateTime(item.atualizadoEm)}</small>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="cw-panel cw-table-panel">
          <div className="cw-panel-header">
            <div>
              <h2>Fila de cobrança</h2>
              <p>Últimos registros da fila conforme filtros aplicados.</p>
            </div>
          </div>

          <div className="cw-table-wrap">
            <table className="cw-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Cliente</th>
                  <th>Telefone</th>
                  <th>Etapa</th>
                  <th>Valor</th>
                  <th>Vencimento</th>
                  <th>Status</th>
                  <th>Twilio SID</th>
                  <th>Atualizado</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {fila.length === 0 && (
                  <tr>
                    <td colSpan="10" className="cw-table-empty">
                      Nenhum registro encontrado.
                    </td>
                  </tr>
                )}

                {fila.map((item) => (
                  <tr key={item.id}>
                    <td>#{item.id}</td>
                    <td>
                      <div className="cw-table-client">
                        <strong>{item.nomeCliente || "-"}</strong>
                        <span>
                          Licença {item.nrLicenca || "-"} • Cliente{" "}
                          {item.idCliente || "-"}
                        </span>
                      </div>
                    </td>
                    <td>{item.telefone || "-"}</td>
                    <td>
                      <span className="cw-etapa-chip">{formatEtapaRegua(item.etapaRegua)}</span>
                    </td>
                    <td>{formatMoney(item.valor)}</td>
                    <td>{formatDateTime(item.vencimento)}</td>
                    <td>
                      <StatusBadge status={item.status} />
                    </td>
                    <td>
                      <span className="cw-sid">{item.twilioSid || "-"}</span>
                    </td>
                    <td>{formatDateTime(item.atualizadoEm || item.criadoEm)}</td>
                    <td>
                      <div className="cw-actions-inline">
                        <button
                          type="button"
                          className="cw-mini-btn"
                          title="Ver timeline"
                          onClick={() => carregarTimeline(item)}
                        >
                          <History size={14} />
                        </button>

                        {item.status === "ERRO" && (
                          <button
                            type="button"
                            className="cw-mini-btn warning"
                            title="Reprocessar item"
                            onClick={() => reprocessarItem(item.id)}
                          >
                            <RefreshCw size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {modalOperacao && (
          <div className="cw-modal-overlay" onClick={fecharModalOperacao}>
            <div className="cw-modal cw-modal-small" onClick={(event) => event.stopPropagation()}>
              <div className="cw-modal-header">
                <div>
                  <h2>
                    {modalOperacao === "incluirBlacklist" && "Incluir na blacklist"}
                    {modalOperacao === "removerBlacklist" && "Remover da blacklist"}
                    {modalOperacao === "config" && "Configuração operacional"}
                    {modalOperacao === "processar" && "Processamento manual"}
                  </h2>

                  <p>
                    {modalOperacao === "incluirBlacklist" &&
                      "Bloqueia um telefone para impedir novos envios automáticos."}
                    {modalOperacao === "removerBlacklist" &&
                      "Libera um telefone bloqueado para voltar a receber cobranças."}
                    {modalOperacao === "config" &&
                      "Controle rápido de envio ativo e limite de processamento."}
                    {modalOperacao === "processar" &&
                      "Executa manualmente o processamento de mensagens pendentes."}
                  </p>
                </div>

                <button
                  type="button"
                  className="cw-close-btn"
                  onClick={fecharModalOperacao}
                  disabled={modalBusy}
                >
                  ×
                </button>
              </div>

              {modalOperacao === "incluirBlacklist" && (
                <form className="cw-modal-form" onSubmit={incluirBlacklistManual}>
                  <label>
                    Telefone
                    <input
                      type="text"
                      value={formBlacklist.telefone}
                      onChange={(event) =>
                        setFormBlacklist((prev) => ({
                          ...prev,
                          telefone: event.target.value,
                        }))
                      }
                      placeholder="Ex.: 5511999999999"
                    />
                  </label>

                  <label>
                    Motivo
                    <select
                      value={formBlacklist.motivo}
                      onChange={(event) =>
                        setFormBlacklist((prev) => ({
                          ...prev,
                          motivo: event.target.value,
                        }))
                      }
                    >
                      <option value="SOLICITACAO_CLIENTE">Solicitação do cliente</option>
                      <option value="OPT_OUT">Opt-out</option>
                      <option value="NUMERO_INVALIDO">Número inválido</option>
                      <option value="BLOQUEIO_MANUAL">Bloqueio manual</option>
                      <option value="LGPD">LGPD</option>
                    </select>
                  </label>

                  <label>
                    Observação
                    <textarea
                      rows="3"
                      value={formBlacklist.observacao}
                      onChange={(event) =>
                        setFormBlacklist((prev) => ({
                          ...prev,
                          observacao: event.target.value,
                        }))
                      }
                      placeholder="Descreva o motivo operacional do bloqueio."
                    />
                  </label>

                  <div className="cw-modal-actions">
                    <button type="button" className="cw-btn" onClick={fecharModalOperacao}>
                      Cancelar
                    </button>

                    <button type="submit" className="cw-btn danger-outline" disabled={modalBusy}>
                      {modalBusy ? <Loader2 size={16} /> : <Ban size={16} />}
                      Bloquear telefone
                    </button>
                  </div>
                </form>
              )}

              {modalOperacao === "removerBlacklist" && (
                <form className="cw-modal-form" onSubmit={removerBlacklistManual}>
                  <label>
                    Telefone
                    <input
                      type="text"
                      value={formRemoverBlacklist.telefone}
                      onChange={(event) =>
                        setFormRemoverBlacklist({
                          telefone: event.target.value,
                        })
                      }
                      placeholder="Ex.: 5511999999999"
                    />
                  </label>

                  <div className="cw-modal-note">
                    Ao remover, o telefone volta a ser elegível para envio conforme a fila e a régua.
                  </div>

                  <div className="cw-modal-actions">
                    <button type="button" className="cw-btn" onClick={fecharModalOperacao}>
                      Cancelar
                    </button>

                    <button type="submit" className="cw-btn secondary" disabled={modalBusy}>
                      {modalBusy ? <Loader2 size={16} /> : <ShieldCheck size={16} />}
                      Liberar telefone
                    </button>
                  </div>
                </form>
              )}

              {modalOperacao === "config" && (
                <form className="cw-modal-form" onSubmit={salvarConfigOperacional}>
                  <label>
                    Envio ativo
                    <select
                      value={formConfig.envioAtivo}
                      onChange={(event) =>
                        setFormConfig((prev) => ({
                          ...prev,
                          envioAtivo: event.target.value,
                        }))
                      }
                    >
                      <option value="N">Não - pausar envio</option>
                      <option value="S">Sim - liberar envio</option>
                    </select>
                  </label>

                  <label>
                    Limite por lote
                    <input
                      type="number"
                      min="1"
                      max="200"
                      value={formConfig.limiteLote}
                      onChange={(event) =>
                        setFormConfig((prev) => ({
                          ...prev,
                          limiteLote: event.target.value,
                        }))
                      }
                    />
                  </label>

                  <div className="cw-modal-note">
                    Esta ação atualiza a configuração operacional no backend. O worker automático continua obedecendo as flags do ambiente.
                  </div>

                  <div className="cw-modal-actions">
                    <button type="button" className="cw-btn" onClick={fecharModalOperacao}>
                      Cancelar
                    </button>

                    <button type="submit" className="cw-btn secondary" disabled={modalBusy}>
                      {modalBusy ? <Loader2 size={16} /> : <Save size={16} />}
                      Salvar configuração
                    </button>
                  </div>
                </form>
              )}

              {modalOperacao === "processar" && (
                <form className="cw-modal-form" onSubmit={processarFilaPeloModal}>
                  <label>
                    Quantidade de mensagens
                    <input
                      type="number"
                      min="1"
                      max="200"
                      value={formProcessamento.limit}
                      onChange={(event) =>
                        setFormProcessamento({
                          limit: event.target.value,
                        })
                      }
                    />
                  </label>

                  <div className="cw-modal-note">
                    O envio só ocorre se a configuração operacional e a janela de envio permitirem.
                  </div>

                  <div className="cw-modal-actions">
                    <button type="button" className="cw-btn" onClick={fecharModalOperacao}>
                      Cancelar
                    </button>

                    <button type="submit" className="cw-btn warning" disabled={modalBusy}>
                      {modalBusy ? <Loader2 size={16} /> : <Power size={16} />}
                      Processar agora
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        {timelineAberta && (
          <div
            className="cw-modal-overlay"
            onClick={() => setTimelineAberta(false)}
          >
            <div className="cw-modal" onClick={(event) => event.stopPropagation()}>
              <div className="cw-modal-header">
                <div>
                  <h2>Timeline #{filaSelecionada?.id || filaSelecionada || "-"}</h2>
                  <p>Histórico operacional da cobrança WhatsApp.</p>
                </div>

                <button
                  type="button"
                  className="cw-close-btn"
                  onClick={() => setTimelineAberta(false)}
                >
                  ×
                </button>
              </div>

              {filaSelecionada?.nomeCliente && (
                <div className="cw-modal-summary">
                  <strong>{filaSelecionada.nomeCliente}</strong>
                  <span>{filaSelecionada.telefone || "-"}</span>
                  <StatusBadge status={filaSelecionada.status} />
                </div>
              )}

              <div className="cw-timeline">
                {loadingTimeline && (
                  <div className="cw-empty cw-empty-inline">
                    <Loader2 size={18} />
                    Carregando timeline...
                  </div>
                )}

                {!loadingTimeline && timeline.length === 0 && (
                  <div className="cw-empty">Nenhum evento localizado para este item.</div>
                )}

                {!loadingTimeline &&
                  timeline.map((evento, index) => (
                    <div key={evento.id || index} className="cw-timeline-item">
                      <div className="cw-timeline-dot" />

                      <div className="cw-timeline-content">
                        <div className="cw-timeline-title-row">
                          <strong>{evento.evento || evento.statusTwilio || "Evento"}</strong>
                          <small>{formatDateTime(evento.criadoEm || evento.enviadoEm || evento.atualizadoEm)}</small>
                        </div>

                        <span>
                          {getErroOperacionalResumo(
                            evento,
                            evento.mensagem || evento.statusNovo || evento.statusTwilio || "-",
                          )}
                        </span>

                        <TwilioErrorInsight source={evento} />

                        {(evento.statusAnterior || evento.statusNovo || evento.statusTwilio) && (
                          <div className="cw-timeline-meta">
                            {evento.statusAnterior && <em>Antes: {evento.statusAnterior}</em>}
                            {evento.statusNovo && <em>Depois: {evento.statusNovo}</em>}
                            {evento.statusTwilio && <em>Twilio: {evento.statusTwilio}</em>}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {processing && (
          <div className="cw-processing">
            <TimerReset size={18} />
            Processando fila manualmente...
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default DashboardCobrancaWhatsapp;